// src/components/seo/CompanySEO.tsx

import { Helmet } from 'react-helmet-async';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type FotoEmpresa = {
  url_foto: string;
  destacado?: boolean;
};

type ServicioEmpresa = {
  servicio?: string;
  precio?: number | null;
};

type CategoriaServiciosEmpresa = {
  servicios?: ServicioEmpresa[];
};

type HorarioEmpresa = {
  activo: boolean;
  dia_semana: string;
  hora_inicio: string;
  hora_fin: string;
};

type ValoracionEmpresa = {
  nombre_cliente?: string;
  puntuacion: number;
  comentario?: string;
  fecha_valoracion?: string;
};

type SubcategoriaEmpresa = {
  id_subcategoria?: string;
  subcategoria?: string;
};

type EmpresaSEO = {
  empresa?: string | null;
  descripcion?: string | null;
  ciudad?: string | null;
  categoria?: string | null;
  subcategorias?: SubcategoriaEmpresa[] | null;
  pais?: string | null;
  telefono?: string | null;
  email?: string | null;
  direccion?: string | null;
  localidad?: string | null;
  logo?: string | null;
  web?: string | null;
  instagram?: string | null;
  facebook?: string | null;
  id_empresa?: string | null;
  activo?: boolean | null;
  privada?: boolean | null;
  fotos?: FotoEmpresa[] | null;
  categorias_servicios?: CategoriaServiciosEmpresa[] | null;
  servicios?: ServicioEmpresa[] | null;
  horario_empresa?: HorarioEmpresa[] | null;
  valoraciones_empresa?: ValoracionEmpresa[] | null;
  valoracion_media_empresa?: number | null;
  total_valoraciones_empresa?: number | null;
};

type CompanySEOProps = {
  empresa: EmpresaSEO | null;
  canonicalUrl: string;
  ciudadSlug?: string;
  categoriaSlug?: string;
  noIndex?: boolean;
};

// ─── [1] Mapa categoria_slug → schema.org @type ───────────────────────────────
// Mismo mapa que company-meta.ts — mantener sincronizados.
const SCHEMA_TYPE_MAP: Record<string, string> = {
  peluqueria: 'HairSalon',
  estetica: 'BeautySalon',
  'tatuajes-y-piercing': 'TattooParlor',
  'maquillaje-y-cosmetica': 'BeautySalon',
  'spa-y-bienestar': 'DaySpa',
  'otros-servicios-personales': 'LocalBusiness',
  dentistas: 'Dentist',
  fisioterapia: 'Physiotherapist',
  'psicologia-y-terapias': 'MedicalBusiness',
  'nutricion-y-dietetica': 'DietNutrition',
  'limpieza-y-mantenimiento': 'HouseCleaning',
  'fontaneria-y-electricidad': 'Plumber',
  'carpinteria-y-herreria': 'HomeAndConstructionBusiness',
  'jardineria-y-paisajismo': 'LandscapeService',
  'pintura-y-remodelacion': 'HousePainter',
  'clases-y-tutorias': 'EducationalOrganization',
  idiomas: 'LanguageSchool',
  'formacion-profesional': 'EducationalOrganization',
  'arte-y-musica': 'PerformingArtsTheater',
  'informatica-y-soporte-tecnico': 'ComputerStore',
  'diseno-y-desarrollo-web': 'ProfessionalService',
  'fotografia-y-video': 'ProfessionalService',
  'electronica-y-moviles': 'ElectronicsStore',
  'taller-mecanico': 'AutoRepair',
  'car-wash-y-detailing': 'AutoWash',
  'transporte-y-mudanzas': 'MovingCompany',
  'restauracion-y-catering': 'FoodEstablishment',
  'eventos-y-organizacion': 'EventPlanner',
  'pasteleria-y-reposteria': 'Bakery',
  veterinaria: 'VeterinaryCare',
  'peluqueria-canina': 'PetStore',
  'entrenamiento-y-paseo': 'PetStore',
  'asesoria-y-consultoria': 'ProfessionalService',
  'marketing-y-publicidad': 'ProfessionalService',
  'seguros-y-finanzas': 'FinancialService',
  'entrenador-personal': 'SportsActivityLocation',
  'gimnasios-y-centros-fitness': 'HealthClub',
  'yoga-y-pilates': 'SportsActivityLocation',
};

function getSchemaType(slug?: string): string {
  if (!slug) return 'LocalBusiness';
  return SCHEMA_TYPE_MAP[slug] ?? 'LocalBusiness';
}

// ─── [3] PriceRange armonizado con edge function ──────────────────────────────
// Google espera €/€€/€€€/€€€€, no "Desde 20€"
function inferPriceRange(precios: number[]): string | undefined {
  if (!precios.length) return undefined;
  const media = precios.reduce((a, b) => a + b, 0) / precios.length;
  if (media < 20) return '€';
  if (media < 50) return '€€';
  if (media < 100) return '€€€';
  return '€€€€';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DIA_TO_SCHEMA: Record<string, string> = {
  lunes: 'Monday',
  martes: 'Tuesday',
  miércoles: 'Wednesday',
  miercoles: 'Wednesday',
  jueves: 'Thursday',
  viernes: 'Friday',
  sábado: 'Saturday',
  sabado: 'Saturday',
  domingo: 'Sunday',
};

function desSlugify(slug: string): string {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function CompanySEO({
  empresa,
  canonicalUrl,
  ciudadSlug,
  categoriaSlug,
  noIndex = false,
}: CompanySEOProps) {
  if (!empresa) return null;

  const origin = 'https://bookoll.com';

  const nombre = empresa.empresa ?? '';
  const descripcion = empresa.descripcion ?? '';
  const ciudad = empresa.ciudad ?? '';
  const pais = empresa.pais ?? 'España';
  const telefono = empresa.telefono ?? '';
  const email = empresa.email ?? '';

  // ── Imagen OG ──────────────────────────────────────────────────────────────
  const fotoDestacada =
    empresa.fotos?.find((f) => f.destacado)?.url_foto ??
    empresa.fotos?.[0]?.url_foto ??
    empresa.logo ??
    `${origin}/og-home.jpg`;

  const todasLasFotos = empresa.fotos?.map((f) => f.url_foto) ?? [];

  // ── Servicios ──────────────────────────────────────────────────────────────
  const allServiceNames = [
    ...(empresa.categorias_servicios ?? []).flatMap((c) =>
      (c.servicios ?? []).map((s) => s.servicio)
    ),
    ...(empresa.servicios ?? []).map((s) => s.servicio),
  ].filter(Boolean) as string[];

  // ── Meta description ───────────────────────────────────────────────────────
  const metaDescription = descripcion
    ? `${descripcion.slice(0, 140)}${
        descripcion.length > 140 ? '…' : ''
      } Información, servicios, horarios y opiniones de ${nombre}${
        ciudad ? ` en ${ciudad}` : ''
      }.`
    : `${nombre}${ciudad ? ` en ${ciudad}` : ''}. ${
        allServiceNames.slice(0, 4).join(', ') || 'Servicios profesionales'
      }. Información, horarios y opiniones en Bookoll.`;

  const subcatNames = (empresa.subcategorias ?? [])
    .map((s) => s.subcategoria)
    .filter(Boolean) as string[];
  const subcatsStr = subcatNames.slice(0, 3).join(', ');

  // ── [Bloque 2] title mejorado con ciudad destacada ────────────────────────
  const pageTitle = `${nombre}${ciudad ? ` — ${ciudad}` : ''}${
    subcatsStr ? ` · ${subcatsStr}` : ''
  } | Reserva cita online · Bookoll`;

  const keywords = [
    nombre,
    ciudad,
    ...subcatNames.slice(0, 5),
    ...allServiceNames.slice(0, 8),
    'reserva cita online',
    'Bookoll',
  ]
    .filter(Boolean)
    .join(', ');

  // ── [3] Precios armonizados ────────────────────────────────────────────────
  const allPrices = [
    ...(empresa.categorias_servicios ?? []).flatMap((c) =>
      (c.servicios ?? []).map((s) => s.precio)
    ),
    ...(empresa.servicios ?? []).map((s) => s.precio),
  ].filter((p): p is number => p != null && p > 0);

  const priceRange = inferPriceRange(allPrices);

  // ── Horario ────────────────────────────────────────────────────────────────
  const openingHoursSpec = (empresa.horario_empresa ?? [])
    .filter((h) => h.activo)
    .map((h) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: `https://schema.org/${
        DIA_TO_SCHEMA[h.dia_semana.toLowerCase()] ?? h.dia_semana
      }`,
      opens: h.hora_inicio.slice(0, 5),
      closes: h.hora_fin.slice(0, 5),
    }));

  // ── Catálogo de servicios ──────────────────────────────────────────────────
  const offerCatalog =
    allServiceNames.length > 0
      ? {
          '@type': 'OfferCatalog',
          name: `Servicios de ${nombre}`,
          itemListElement: allServiceNames.slice(0, 20).map((svc, i) => ({
            '@type': 'Offer',
            position: i + 1,
            itemOffered: { '@type': 'Service', name: svc },
          })),
        }
      : undefined;

  // ── Valoraciones ───────────────────────────────────────────────────────────
  const totalValoraciones =
    empresa.total_valoraciones_empresa ??
    empresa.valoraciones_empresa?.length ??
    0;

  const aggregateRating =
    empresa.valoracion_media_empresa && totalValoraciones > 0
      ? {
          '@type': 'AggregateRating',
          ratingValue: empresa.valoracion_media_empresa.toFixed(1),
          reviewCount: totalValoraciones,
          bestRating: '5',
          worstRating: '1',
        }
      : undefined;

  // ── [2] datePublished en reviews ──────────────────────────────────────────
  const reviews = (empresa.valoraciones_empresa ?? []).slice(0, 5).map((v) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: v.nombre_cliente ?? 'Cliente' },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: v.puntuacion,
      bestRating: 5,
      worstRating: 1,
    },
    ...(v.comentario && { reviewBody: v.comentario }),
    // datePublished requerido por Google para mostrar estrellas en rich results
    ...(v.fecha_valoracion && {
      datePublished: v.fecha_valoracion.slice(0, 10),
    }),
  }));

  // ── [1] @type específico por categoría ────────────────────────────────────
  const schemaType = getSchemaType(categoriaSlug);

  // ── JSON-LD LocalBusiness ──────────────────────────────────────────────────
  const localBusiness: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': schemaType,
    '@id': canonicalUrl,
    name: nombre,
    description: metaDescription,
    url: canonicalUrl,
    ...(telefono && { telephone: telefono }),
    ...(email && { email }),
    ...(empresa.logo && { logo: empresa.logo }),
    ...(todasLasFotos.length > 0 && { image: todasLasFotos.slice(0, 6) }),
    // [3] priceRange armonizado: €/€€/€€€/€€€€
    ...(priceRange && { priceRange }),
    address: {
      '@type': 'PostalAddress',
      ...(empresa.direccion && { streetAddress: empresa.direccion }),
      ...(empresa.localidad && { addressLocality: empresa.localidad }),
      ...(empresa.ciudad && { addressRegion: empresa.ciudad }),
      addressCountry: pais === 'España' ? 'ES' : pais,
    },
    ...(openingHoursSpec.length > 0 && {
      openingHoursSpecification: openingHoursSpec,
    }),
    ...(aggregateRating && { aggregateRating }),
    ...(reviews.length > 0 && { review: reviews }),
    ...(offerCatalog && { hasOfferCatalog: offerCatalog }),
    sameAs: [
      ...(empresa.web ? [empresa.web] : []),
      ...(empresa.instagram
        ? [`https://instagram.com/${empresa.instagram.replace('@', '')}`]
        : []),
      ...(empresa.facebook ? [empresa.facebook] : []),
    ].filter(Boolean),
  };

  // ── JSON-LD Breadcrumb ─────────────────────────────────────────────────────
  const categoriaUrl = categoriaSlug ? `${origin}/${categoriaSlug}` : null;
  const ciudadUrl =
    categoriaSlug && ciudadSlug
      ? `${origin}/${categoriaSlug}/${ciudadSlug}`
      : null;
  const categoriaNombre =
    empresa.categoria ?? (categoriaSlug ? desSlugify(categoriaSlug) : null);

  const breadcrumbItems: Array<{
    '@type': string;
    position: number;
    name: string;
    item: string;
  }> = [{ '@type': 'ListItem', position: 1, name: 'Inicio', item: origin }];

  if (categoriaUrl && categoriaNombre) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 2,
      name: categoriaNombre,
      item: categoriaUrl,
    });
  }
  if (ciudadUrl && ciudad) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: ciudad,
      item: ciudadUrl,
    });
  }
  breadcrumbItems.push({
    '@type': 'ListItem',
    position: breadcrumbItems.length + 1,
    name: nombre,
    item: canonicalUrl,
  });

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={keywords} />
      <meta
        name="robots"
        content={
          noIndex
            ? 'noindex, nofollow'
            : 'index, follow, max-snippet:-1, max-image-preview:large'
        }
      />
      <link rel="canonical" href={canonicalUrl} />
      {/* [12] Solo x-default — sin hreflang duplicado */}
      {!noIndex && (
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="business.business" />
      <meta property="og:site_name" content="Bookoll" />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fotoDestacada} />
      <meta property="og:image:secure_url" content={fotoDestacada} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={pageTitle} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@bookoll" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={fotoDestacada} />
      <meta name="twitter:image:alt" content={pageTitle} />

      {/* JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(localBusiness)}
      </script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
    </Helmet>
  );
}
